import React, { ReactNode, useCallback, useMemo } from "react";

import { bem } from "@react-md/utils";
import { MenuProps } from "antd/es/menu";

import { EntityType } from "@mapmycustomers/shared";
import FieldCategory from "@mapmycustomers/shared/enum/fieldModel/FieldCategory";
import { EntityTypeSupportingPinnedFields } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import IFieldModel, {
  CategorizedFields,
} from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import { useConfigProvider } from "../ConfigProvider/";
import Dropdown from "../Dropdown";
import EntityTypeIcon from "../EntityTypeShapedIcon";

import EntityFieldsList from "./EntityFieldsList";

const block = bem("mmc-entity-field-selector");

const yes = () => true;

interface EntityFieldSelectorProps {
  addedFields: IField[];
  children: ReactNode;
  entityFieldModels: Record<EntityTypeSupportingPinnedFields, IFieldModel>;
  fieldFilter?: (field: IField) => boolean;
  onChange?: (field: IField, entityType: EntityTypeSupportingPinnedFields) => void;
  value?: IField;
}

const EntityFieldSelector = ({
  addedFields,
  children,
  entityFieldModels,
  fieldFilter = yes,
  onChange,
  value,
}: EntityFieldSelectorProps) => {
  const configProvider = useConfigProvider();
  const [open, show, close] = useBoolean(false);

  const handleOpenChange = useCallback((open: boolean) => (open ? show : close)(), [close, show]);

  const entityFields: Partial<
    Record<EntityTypeSupportingPinnedFields, Partial<CategorizedFields>>
  > = useMemo(
    () =>
      (
        [
          EntityType.COMPANY,
          EntityType.PERSON,
          EntityType.DEAL,
          EntityType.ACTIVITY,
        ] as EntityTypeSupportingPinnedFields[]
      ).reduce((result, entityType) => {
        const fieldModel = entityFieldModels[entityType];
        const fields = (Object.keys(fieldModel.categorizedFields) as FieldCategory[]).reduce(
          (result, category) => ({
            ...result,
            [category]: fieldModel.categorizedFields[category].filter(
              (field: IField) =>
                field.isReadable &&
                field.isCustomizableField &&
                fieldFilter(field) &&
                (field.name === value?.name || !addedFields.includes(field))
            ),
          }),
          {}
        );
        return { ...result, [entityType]: fields };
      }, {}),
    [addedFields, entityFieldModels, fieldFilter, value?.name]
  );

  const items: MenuProps["items"] = useMemo(
    () => [
      {
        children: [
          {
            className: block("sub-menu"),
            disabled: true,
            key: "companyFields",
            label: (
              <EntityFieldsList
                editableFields={entityFields[EntityType.COMPANY]}
                entityType={EntityType.COMPANY}
                onChange={onChange}
                onCloseDropdown={close}
              />
            ),
          },
        ],
        className: block("entity"),
        key: "company",
        label: (
          <div className={block("label")}>
            <EntityTypeIcon
              className={block("entity-icon")}
              entityType={EntityType.COMPANY}
              size="2xs"
            />
            <span>{configProvider.formatMessage("ui.entityFieldSelector.company")}</span>
          </div>
        ),
      },
      {
        children: [
          {
            className: block("sub-menu"),
            disabled: true,
            key: "personFields",
            label: (
              <EntityFieldsList
                editableFields={entityFields[EntityType.PERSON]}
                entityType={EntityType.PERSON}
                onChange={onChange}
                onCloseDropdown={close}
              />
            ),
          },
        ],
        className: block("entity"),
        key: "person",
        label: (
          <div className={block("label")}>
            <EntityTypeIcon
              className={block("entity-icon")}
              entityType={EntityType.PERSON}
              size="2xs"
            />
            <span>{configProvider.formatMessage("ui.entityFieldSelector.person")}</span>
          </div>
        ),
      },
      {
        children: [
          {
            className: block("sub-menu"),
            disabled: true,
            key: "dealFields",
            label: (
              <EntityFieldsList
                editableFields={entityFields[EntityType.DEAL]}
                entityType={EntityType.DEAL}
                onChange={onChange}
                onCloseDropdown={close}
              />
            ),
          },
        ],
        className: block("entity"),
        key: "deal",
        label: (
          <div className={block("label")}>
            <EntityTypeIcon
              className={block("entity-icon")}
              entityType={EntityType.DEAL}
              size="2xs"
            />
            <span>{configProvider.formatMessage("ui.entityFieldSelector.deal")}</span>
          </div>
        ),
      },
      {
        children: [
          {
            className: block("sub-menu"),
            disabled: true,
            key: "activityFields",
            label: (
              <EntityFieldsList
                editableFields={entityFields[EntityType.ACTIVITY]}
                entityType={EntityType.ACTIVITY}
                onChange={onChange}
                onCloseDropdown={close}
              />
            ),
          },
        ],
        className: block("entity"),
        key: "activity",
        label: (
          <div className={block("label")}>
            <EntityTypeIcon
              className={block("entity-icon")}
              entityType={EntityType.ACTIVITY}
              size="2xs"
            />
            <span>{configProvider.formatMessage("ui.entityFieldSelector.activity")}</span>
          </div>
        ),
      },
    ],
    [close, configProvider, entityFields, onChange]
  );

  return (
    <Dropdown
      className={block()}
      menu={{
        className: block("menu"),
        items,
        openKeys: open ? undefined : [],
        selectable: true,
        triggerSubMenuAction: "click",
      }}
      onOpenChange={handleOpenChange}
      open={open}
      trigger={["click"]}
    >
      {children}
    </Dropdown>
  );
};

export default EntityFieldSelector;
